/**
 * "This device" — everything about the PHONE a dispatcher is holding, rather
 * than about the trips on it: the Home Screen icon, and notifications.
 *
 * THREE PARTS, ONE SUBJECT
 * ------------------------
 *   the bell in the top bar   the permanent way in, on every dispatcher
 *                             screen, whatever has been dismissed
 *   the nudge under the bar   ONE thing, once, after signing in: the Home
 *                             Screen offer while there is one to make, and
 *                             the notifications ask once there is not.
 *                             Dismissible, remembered per device, and never
 *                             two cards nagging for one act
 *   the sheet                 the offer again, whenever they want it, with
 *                             the permanent on/off switch under it
 *
 * WHY THEY ARE SEQUENCED THAT WAY. On an iPhone, Web Push exists only inside
 * the Home Screen app — so asking a dispatcher in Safari to allow
 * notifications would be asking for something the browser cannot give.
 * `PushSetting` already knows that (`supportState` →
 * `IOS_NEEDS_HOME_SCREEN`), and the one-time ask stays silent whenever a
 * prompt could not be raised. So the order falls out of the truth rather
 * than out of a rule here: install first, ask once it can succeed.
 *
 * NOTIFICATIONS ARE THE DASHBOARD'S, NOT A SECOND SYSTEM. A push is the
 * office bell's twin — the same rows, governed by the same per-type
 * preferences a dispatcher already has on the dashboard. This screen
 * registers a DEVICE (`officePushApi`, the very API the dashboard's own card
 * uses) and says where the preferences live. It does not offer a second set.
 */
import React, { useState } from 'react';
import BottomSheet from '../Components/BottomSheet';
import PushSetting from '../Components/PushSetting';
import PushAsk, { PUSH_ASK_KEYS } from '../Components/PushAsk';
import AddToHomeOffer, { ADD_TO_HOME_COPY, useAddToHomeOffer } from '../Components/AddToHomeOffer';
import { AddToHomeIcon, BellIcon } from '../Components/Icons';
import { INSTALL_OFFERS } from '../installContext';
import { PUSH_COPY_EN } from '../Operator/operatorFormat';
import { officePushApi } from '../../../Services/NotificationService';

const SHEET_TITLE = 'This device';
const SHEET_SUBTITLE = 'Notifications, and the board on your Home Screen';
const BELL_LABEL = 'Notifications on this device';
const NUDGE_TEXT = 'Put the dispatch board on your Home Screen';
const SHOW_ME = 'Show me how';
const NOT_NOW = 'Not now';

/** Where the per-type preferences really live, said in as many words. */
const PREFERENCES_HINT = 'Which notifications reach you is set on the dashboard, under '
  + 'Notification settings — there is one set of preferences, not two.';

/**
 * The state behind all three parts. One hook, called once by the layout, so
 * the bell, the nudge and the sheet can never disagree about what this
 * browser can do.
 */
export function useDispatcherDevice() {
  const {
    offer, dismissed, dismiss, install,
  } = useAddToHomeOffer();
  const [open, setOpen] = useState(false);

  return {
    offer,
    install,
    open,
    show: () => setOpen(true),
    close: () => setOpen(false),
    /** Is there still a Home Screen offer to make on this device? */
    offering: offer !== INSTALL_OFFERS.NONE && !dismissed,
    dismiss,
  };
}

/** The permanent way back to all of it: a 44px bell in the dark top bar. */
export function DispatcherDeviceButton({ device }) {
  return (
    <button
      type="button"
      className="pp-dispatch__bell"
      aria-label={BELL_LABEL}
      aria-haspopup="dialog"
      aria-expanded={device.open}
      onClick={device.show}
    >
      <BellIcon size={20} />
    </button>
  );
}

/**
 * The one thing said once, under the bar — and NEVER in the way: a single
 * line with a 44px control at each end, which a tap on "Not now" removes for
 * good on this device.
 *
 * Where the browser gives us a real install prompt, the button IS that
 * prompt. Where it does not, the button opens the sheet and the steps are
 * written out; there is no fake "Install" anywhere.
 */
export function DispatcherDeviceNudge({ device }) {
  if (!device.offering) {
    // Nothing left to install, or it has been waved away: the other half of
    // the same subject, which is silent unless it can really succeed.
    return (
      <PushAsk offerKey={PUSH_ASK_KEYS.DISPATCHER} copy={PUSH_COPY_EN} api={officePushApi} />
    );
  }

  const prompts = device.offer === INSTALL_OFFERS.PROMPT;

  return (
    <div className="pp-dispatch__nudge">
      <AddToHomeIcon className="pp-dispatch__nudge-icon" size={20} />
      <span className="pp-dispatch__nudge-text">{NUDGE_TEXT}</span>
      <button
        type="button"
        className="pp-btn pp-btn--sm pp-btn--primary"
        onClick={prompts ? device.install : device.show}
      >
        {prompts ? ADD_TO_HOME_COPY.install : SHOW_ME}
      </button>
      <button
        type="button"
        className="pp-btn pp-btn--sm pp-dispatch__nudge-quiet"
        onClick={device.dismiss}
      >
        {NOT_NOW}
      </button>
    </div>
  );
}

/** The offer in full, and the switch that outlives it. */
export function DispatcherDeviceSheet({ device }) {
  return (
    <BottomSheet
      title={SHEET_TITLE}
      subtitle={SHEET_SUBTITLE}
      onClose={device.close}
      closeLabel="Close"
    >
      <AddToHomeOffer offer={device.offer} onInstall={device.install} />
      <PushSetting variant="settings" copy={PUSH_COPY_EN} api={officePushApi} />
      <p className="pp-op-form__hint">{PREFERENCES_HINT}</p>
    </BottomSheet>
  );
}
